import type { ReactElement } from 'react';
import type { SkillListStateFilter } from '@kirin/api-client/contracts/skills';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SKILL_LIST_STATE_OPTIONS } from './constants';

interface SkillListStateFilterControlProps {
  value: SkillListStateFilter;
  onChange: (next: SkillListStateFilter) => void;
  /**
   * KIR-4701: a security group's choice reads Block, not Mark for delete, so
   * its scope labels the marked pill "Blocked".
   */
  markedLabel?: string;
}

/**
 * "All | Allowed | Marked for delete" segmented control above the Skills
 * table (KIR-4699). Fully controlled; the server applies the narrowing, so
 * the pill is a query parameter rather than a column filter.
 */
export function SkillListStateFilterControl({
  value,
  onChange,
  markedLabel,
}: SkillListStateFilterControlProps): ReactElement {
  return (
    <div
      role="group"
      aria-label="Filter by list state"
      className="inline-flex items-center"
    >
      {SKILL_LIST_STATE_OPTIONS.map((option, index) => {
        const selected = option.value === value;
        return (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant={selected ? 'default' : 'outline'}
            aria-pressed={selected}
            onClick={() => {
              if (!selected) onChange(option.value);
            }}
            className={cn(
              'h-8 rounded-none px-3 text-xs font-medium shadow-none',
              index === 0 && 'rounded-l-md',
              index === SKILL_LIST_STATE_OPTIONS.length - 1 && 'rounded-r-md',
              index > 0 && '-ml-px',
              selected && 'relative z-10'
            )}
          >
            {option.value === 'marked_for_delete' && markedLabel
              ? markedLabel
              : option.label}
          </Button>
        );
      })}
    </div>
  );
}
